"use client";

import React, { useCallback, useRef, useState, useEffect } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import ResizableImage from "@/OS/components/apps/extensions/ResizableImage";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Youtube from '@tiptap/extension-youtube';
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import FontSize from "@tiptap/extension-font-size";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import {
  Bold,
  Strikethrough,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Link,
  Image as ImageIcon,
  Loader2,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  Wrench,
} from "lucide-react";
import { Toggle } from "@/components/ui/toggle";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/OS/components/ui/dialog";
import { BubbleMenu } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { generateImage } from "@/lib/openai";

type Props = {
  value: string;
  onChange: (richText: string) => void;
  disabled?: boolean;
  placeholder?: string;
  variant?: 'default' | 'overlay';
  showToolbar?: boolean;
  onEditorReady?: (editor: Editor | null) => void;
  textColor?: string;
  contentKey?: string | number; // external sync key
};

const Toolbar = ({ editor, onAddImage, isUploadingImage, variant = 'default', onOpenImageTools }: { editor: Editor | null; onAddImage: () => void; isUploadingImage: boolean; variant?: 'default' | 'overlay'; onOpenImageTools: () => void; }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className={
        variant === 'overlay'
          ? "border border-input bg-background/60 backdrop-blur rounded-md p-1 flex flex-wrap items-center gap-1 shadow-sm"
          : "border border-input bg-transparent rounded-t-md p-1 flex flex-wrap items-center gap-1"
      }
    >
      <Toggle size="sm" pressed={editor.isActive("heading", { level: 1 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
        <Heading1 className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("heading", { level: 2 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("heading", { level: 3 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("bold")} onPressedChange={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("italic")} onPressedChange={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("strike")} onPressedChange={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("bulletList")} onPressedChange={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("orderedList")} onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("codeBlock")} onPressedChange={() => editor.chain().focus().toggleCodeBlock().run()}>
        <Code className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("subscript")} onPressedChange={() => editor.chain().focus().toggleSubscript().run()}>
        <SubscriptIcon className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("superscript")} onPressedChange={() => editor.chain().focus().toggleSuperscript().run()}>
        <SuperscriptIcon className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" onPressedChange={onAddImage} disabled={isUploadingImage}>
        {isUploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("link")} onPressedChange={setLink}>
        <Link className="h-4 w-4" />
      </Toggle>
      {/* NEW: Image tools button shows only when an image is selected */}
      {editor.isActive("resizableImage") && (
        <Toggle size="sm" onPressedChange={onOpenImageTools}>
          <Wrench className="h-4 w-4" />
        </Toggle>
      )}
    </div>
  );
};

const RichTextEditor = ({ value, onChange, disabled, placeholder, variant = 'default', showToolbar = true, onEditorReady, textColor, contentKey }: Props) => {
  const { toast } = useToast();
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImageTools, setShowImageTools] = useState(false);

  // NEW: suppress onUpdate when content is set programmatically
  const suppressExternalSyncRef = React.useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: {
          HTMLAttributes: {
            class: "bg-muted text-muted-foreground rounded-sm p-2 font-mono text-sm",
          },
        },
        link: {
          openOnClick: false,
          autolink: true,
        },
      }),
      // Style marks
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // Use resizable image node view
      ResizableImage.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Subscript,
      Superscript,
      Youtube.configure({
        controls: true,
        modestBranding: true,
        rel: false,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      // Ignore updates caused by setContent when switching slides
      if (suppressExternalSyncRef.current) return;
      onChange(editor.getHTML());
    },
    editable: !disabled,
    editorProps: {
      attributes: {
        class:
          variant === 'overlay'
            ? "h-full w-full bg-transparent border-0 p-0 text-[1em] leading-normal outline-none ring-0 focus-visible:outline-none"
            : "prose dark:prose-invert max-w-none min-h-[250px] w-full rounded-b-md border-input border-t-0 border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        style: variant === 'overlay' ? 'color: inherit;' : undefined,
      },
    },
  });

  useEffect(() => {
    if (onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  // Only re-sync content when contentKey changes; suppress onUpdate during this operation
  useEffect(() => {
    if (!editor) return;
    suppressExternalSyncRef.current = true;
    editor.commands.setContent(value, false);
    setTimeout(() => {
      suppressExternalSyncRef.current = false;
    }, 0);
  }, [editor, contentKey]);

  // Apply initial content once if the editor mounts empty but we already have non-empty HTML
  useEffect(() => {
    if (!editor) return;
    const hasValue = typeof value === "string" && value.trim().length > 0;
    if (!hasValue) return;
    const editorEmpty = editor.getText().trim().length === 0;
    if (editorEmpty) {
      suppressExternalSyncRef.current = true;
      editor.commands.setContent(value, false);
      setTimeout(() => {
        suppressExternalSyncRef.current = false;
      }, 0);
    }
  }, [editor, value]);

  // Guarded re-sync: if not focused and editor is empty or out-of-sync, set content.
  // This prevents the initial blank state without moving the caret during typing.
  useEffect(() => {
    if (!editor) return;
    const hasValue = typeof value === "string" && value.trim().length > 0;
    if (!hasValue) return;
    const focused = editor.isFocused;
    const editorTextEmpty = editor.getText().trim().length === 0;
    const htmlDiffers = editor.getHTML() !== value;
    if (!focused && (editorTextEmpty || htmlDiffers)) {
      suppressExternalSyncRef.current = true;
      editor.commands.setContent(value, false);
      setTimeout(() => {
        suppressExternalSyncRef.current = false;
      }, 0);
    }
  }, [editor, value]);

  // Close image tools if selection moves away from the image, but only while the editor is focused
  useEffect(() => {
    if (!editor) return;
    const onSel = () => {
      if (!showImageTools) return;
      // Prevent auto-close when the dialog has focus (editor not focused)
      if (!editor.isFocused) return;
      if (!editor.isActive("resizableImage")) {
        setShowImageTools(false);
      }
    };
    editor.on("selectionUpdate", onSel);
    return () => editor.off("selectionUpdate", onSel);
  }, [editor, showImageTools]);

  // Open image tools when node view wrench dispatches the event
  useEffect(() => {
    const openHandler = () => setShowImageTools(true);
    window.addEventListener('rte-open-image-tools', openHandler as EventListener);
    return () => {
      window.removeEventListener('rte-open-image-tools', openHandler as EventListener);
    };
  }, []);

  // Build a prompt from nearby slide text
  const buildImagePrompt = useCallback(() => {
    if (!editor) return "A relevant, high-quality illustrative image for a presentation slide.";
    // Use nearest heading plus first 300 chars of text to guide image generation
    const docText = editor.getText();
    const headingMatch = docText.match(/^(?:#+\s*)(.+)$/m);
    const heading = headingMatch ? headingMatch[1].trim() : "";
    const snippet = docText.replace(/^\s*#+\s+.*$/gm, "").trim().slice(0, 300);
    const base = heading ? `Slide title: "${heading}".` : "Presentation slide.";
    return `${base} Content context: ${snippet}. Create a tasteful, presentation-ready illustrative image (no text on image).`;
  }, [editor]);

  const handleGenerateAIImage = useCallback(async () => {
    if (!editor) return;
    const prompt = buildImagePrompt();
    toast({ title: "Generating image...", description: "This can take a few seconds." });
    const url = await generateImage(prompt);
    // Do not force editor focus; just update attributes
    editor.chain().updateAttributes("resizableImage", { src: url }).run();
    toast({ title: "Image updated", description: "Replaced with AI-generated image." });
  }, [editor, buildImagePrompt, toast]);

  const handleImageUpload = useCallback(async (file: File) => {
    setIsUploadingImage(true);
    try {
      const BUCKET = 'rich-text-images';
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const safeFileName = `rte-image-${Date.now()}.${ext}`.replace(/[^a-z0-9.\-_]/gi, '_');

      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(safeFileName, file, {
          upsert: true,
          contentType: file.type || 'image/png',
          cacheControl: '3600',
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage.from(BUCKET).getPublicUrl(safeFileName);
      if (publicUrlData?.publicUrl) {
        if (editor?.isActive("resizableImage")) {
          editor.chain().focus().updateAttributes("resizableImage", { src: publicUrlData.publicUrl }).run();
        } else {
          // Default alt to empty; modal tools will sync and let the user set it
          editor?.chain().focus().insertContent(`<img src="${publicUrlData.publicUrl}" alt="" />`).run();
        }
        toast({ title: "Image uploaded successfully!" });
      } else {
        throw new Error("Could not get public URL for uploaded image.");
      }
    } catch (err: any) {
      console.error("Image upload failed:", err);
      toast({
        title: "Image upload failed",
        description: err.message || "Could not upload image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [editor, toast]);

  const triggerImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Image tools state and helpers
  const [gamma, setGamma] = useState(1);        // overall exposure factor
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);
  const [saturation, setSaturation] = useState(1);
  const [blur, setBlur] = useState(0);
  const [radius, setRadius] = useState(8);
  const [altText, setAltText] = useState("");

  // Keep controls in sync with the selected image's current attributes
  useEffect(() => {
    if (!editor) return;
    const readFromSelection = () => {
      const sel: any = editor.state.selection as any;
      const node = sel?.node;
      if (node && node.type?.name === 'resizableImage') {
        const style: string = node.attrs?.style || '';
        const alt: string = node.attrs?.alt || '';
        setAltText(alt);
        const getNum = (re: RegExp, def: number) => {
          const m = style.match(re);
          return m ? parseFloat(m[1]) : def;
        };
        // We can read brightness/contrast/saturate/blur/radius; gamma is an extra multiplier (leave at 1)
        const b = getNum(/brightness\(([\d.]+)\)/, 1);
        const c = getNum(/contrast\(([\d.]+)\)/, 1);
        const s = getNum(/saturate\(([\d.]+)\)/, 1);
        const bl = getNum(/blur\(([\d.]+)px\)/, 0);
        const r = getNum(/border-radius:\s*([\d.]+)px/, 8);
        setBrightness(b);
        setContrast(c);
        setSaturation(s);
        setBlur(bl);
        setRadius(r);
        setGamma(1);
      }
    };
    editor.on('selectionUpdate', readFromSelection);
    return () => {
      editor.off('selectionUpdate', readFromSelection);
    };
  }, [editor]);

  const applyImageStyle = useCallback((next?: Partial<{ gamma: number; brightness: number; contrast: number; saturation: number; blur: number; radius: number; alt: string }>) => {
    const g = next?.gamma ?? gamma;
    const b = next?.brightness ?? brightness;
    const c = next?.contrast ?? contrast;
    const s = next?.saturation ?? saturation;
    const bl = next?.blur ?? blur;
    const r = next?.radius ?? radius;
    const filters = `brightness(${(b * g).toFixed(2)}) contrast(${(c * g).toFixed(2)}) saturate(${s.toFixed(2)}) blur(${bl.toFixed(1)}px)`;
    const style = `max-width:100%;height:auto;filter:${filters};border-radius:${r}px;`;
    // Avoid focus shifting while using sliders
    editor?.chain().updateAttributes("resizableImage", { style }).run();
    if (typeof next?.alt === "string") {
      editor?.chain().updateAttributes("resizableImage", { alt: next.alt }).run();
    }
  }, [editor, gamma, brightness, contrast, saturation, blur, radius]);

  const resetImageStyle = useCallback(() => {
    setGamma(1);
    setBrightness(1);
    setContrast(1);
    setSaturation(1);
    setBlur(0);
    setRadius(8);
    // Clear inline filters and any fixed width so image returns to natural responsive sizing
    editor?.chain().updateAttributes("resizableImage", { style: "", width: null }).run();
  }, [editor]);

  return (
    <div className="flex flex-col justify-stretch h-full">
      {showToolbar && (
        <Toolbar
          editor={editor}
          onAddImage={triggerImageUpload}
          isUploadingImage={isUploadingImage}
          variant={variant}
          onOpenImageTools={() => setShowImageTools(true)}
        />
      )}
      <EditorContent
        editor={editor}
        onPaste={(e) => {
          const items = e.clipboardData?.items;
          if (!items) return;
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type && item.type.startsWith('image/')) {
              const blob = item.getAsFile();
              if (blob) {
                const fileName = `pasted-image-${Date.now()}.${(blob.type.split('/')[1] || 'png')}`;
                const file = new File([blob], fileName, { type: blob.type });
                handleImageUpload(file);
              }
            }
          }
        }}
        className="flex-1 pointer-events-auto"
        style={variant === 'overlay' ? { color: textColor || 'inherit' } : undefined}
      />
      {/* NEW: Modal image tools (consistent with MapleOS modals) */}
      <Dialog open={showImageTools} onOpenChange={setShowImageTools}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Image tools</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 mb-3">
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              Replace
            </Button>
            <Button variant="outline" size="sm" onClick={handleGenerateAIImage}>
              AI Image
            </Button>
            <Button variant="ghost" size="sm" onClick={resetImageStyle}>
              Reset
            </Button>
          </div>

          {/* NEW: Presets row inspired by Gamma */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  // Fit: remove fixed width, keep default look
                  editor?.chain().updateAttributes("resizableImage", { width: null }).run();
                }}
              >
                Fit
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const preset = { gamma: 1.0, brightness: 1.05, contrast: 1.10, saturation: 1.30, blur: 0, radius };
                  setGamma(preset.gamma); setBrightness(preset.brightness); setContrast(preset.contrast); setSaturation(preset.saturation); setBlur(preset.blur);
                  applyImageStyle(preset);
                }}
              >
                Vibrant
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const preset = { gamma: 0.95, brightness: 1.05, contrast: 1.02, saturation: 1.10, blur: 2, radius };
                  setGamma(preset.gamma); setBrightness(preset.brightness); setContrast(preset.contrast); setSaturation(preset.saturation); setBlur(preset.blur);
                  applyImageStyle(preset);
                }}
              >
                Soft focus
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const preset = { gamma: 1.0, brightness: 1.0, contrast: 1.0, saturation: 0.0, blur: 0, radius };
                  setGamma(preset.gamma); setBrightness(preset.brightness); setContrast(preset.contrast); setSaturation(preset.saturation); setBlur(preset.blur);
                  applyImageStyle(preset);
                }}
              >
                Grayscale
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Alt text</Label>
              <Input
                value={altText}
                onChange={(e) => {
                  setAltText(e.target.value);
                  applyImageStyle({ alt: e.target.value });
                }}
                placeholder="Describe the image"
                className="mt-1 h-8"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Gamma</Label>
                <Slider
                  value={[gamma]}
                  min={0.5}
                  max={2}
                  step={0.05}
                  onValueChange={(v) => {
                    setGamma(v[0]);
                    applyImageStyle({ gamma: v[0] });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Brightness</Label>
                <Slider
                  value={[brightness]}
                  min={0.5}
                  max={2}
                  step={0.05}
                  onValueChange={(v) => {
                    setBrightness(v[0]);
                    applyImageStyle({ brightness: v[0] });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Contrast</Label>
                <Slider
                  value={[contrast]}
                  min={0.5}
                  max={2}
                  step={0.05}
                  onValueChange={(v) => {
                    setContrast(v[0]);
                    applyImageStyle({ contrast: v[0] });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Saturation</Label>
                <Slider
                  value={[saturation]}
                  min={0}
                  max={2}
                  step={0.05}
                  onValueChange={(v) => {
                    setSaturation(v[0]);
                    applyImageStyle({ saturation: v[0] });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Blur</Label>
                <Slider
                  value={[blur]}
                  min={0}
                  max={10}
                  step={0.5}
                  onValueChange={(v) => {
                    setBlur(v[0]);
                    applyImageStyle({ blur: v[0] });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Corner radius</Label>
                <Slider
                  value={[radius]}
                  min={0}
                  max={32}
                  step={1}
                  onValueChange={(v) => {
                    setRadius(v[0]);
                    applyImageStyle({ radius: v[0] });
                  }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="default" onClick={() => setShowImageTools(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImageUpload(file);
        }}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default RichTextEditor;